import React from 'react';
import { Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import ActionDialogShell from './ActionDialogShell';
import PublicationSelection from '../components/PublicationSelection';

export default function PublishConfirmDialog({
    open,
    onClose,
    publishConfirmMode,
    publishReasonExtra,
    setPublishReasonExtra,
    publishConfirmText,
    isPending,
    isArticleBodyLoading,
    isError,
    errorText,
    onConfirm,
    // Multi-publication props
    targetMediaOptions,
    onTargetMediaChange,
    effectiveTargetMedia,
    effectiveMultiPublicationTargetMedias,
    availableMultiPublicationTargetMedias,
    canEditFields,
    setArrayField,
    editableFields,
    getTargetMediaLabel,
}) {
    const theme = useTheme();
    const intl = useIntl();
    const extraReasonInputRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const timer = window.setTimeout(() => {
            extraReasonInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [open]);

    const headerBgColor =
        publishConfirmMode === 'draft' ? theme.palette.draft?.main : theme.palette.publish?.main;
    const headerTextColor =
        publishConfirmMode === 'draft'
            ? theme.palette.draft?.contrastText
            : theme.palette.publish?.contrastText;

    return (
        <ActionDialogShell
            open={open}
            onClose={onClose}
            title={
                publishConfirmMode === 'draft'
                    ? intl.formatMessage({ id: 'newsQueue.dialog.draft.title' })
                    : intl.formatMessage({ id: 'newsQueue.dialog.publish.title' })
            }
            headerBgColor={headerBgColor}
            headerTextColor={headerTextColor}
            actions={
                <>
                    <Button disabled={isPending || isArticleBodyLoading} onClick={onClose}>
                        {intl.formatMessage({ id: 'newsQueue.common.cancel' })}
                    </Button>
                    <Button
                        color="primary"
                        sx={{
                            bgcolor: headerBgColor,
                            color: headerTextColor,
                            '&:hover': {
                                bgcolor: headerBgColor,
                            },
                        }}
                        disabled={isPending || isArticleBodyLoading}
                        onClick={onConfirm}
                    >
                        {publishConfirmMode === 'draft'
                            ? intl.formatMessage({ id: 'newsQueue.actions.draft' })
                            : intl.formatMessage({ id: 'newsQueue.actions.publish' })}
                    </Button>
                </>
            }
        >
            <PublicationSelection
                open={open}
                effectiveTargetMedia={effectiveTargetMedia}
                targetMediaOptions={targetMediaOptions}
                onTargetMediaChange={onTargetMediaChange}
                effectiveMultiPublicationTargetMedias={effectiveMultiPublicationTargetMedias}
                availableMultiPublicationTargetMedias={availableMultiPublicationTargetMedias}
                canEditFields={canEditFields}
                isPending={isPending || isArticleBodyLoading}
                setArrayField={setArrayField}
                editableFields={editableFields}
                getTargetMediaLabel={getTargetMediaLabel}
            />
            <TextField
                autoFocus
                inputRef={extraReasonInputRef}
                margin="dense"
                id="publish-extra-confirm"
                label={intl.formatMessage({ id: 'newsQueue.feedback.extraLabel' })}
                type="text"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={publishReasonExtra}
                onChange={(e) => setPublishReasonExtra(e.target.value)}
                helperText={intl.formatMessage({ id: 'newsQueue.feedback.optionalHelper' })}
                sx={{ mt: 2 }}
            />

            <Typography variant="body2">{publishConfirmText}</Typography>
            {isError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {String(errorText ?? '').trim() ||
                        intl.formatMessage({ id: 'newsQueue.publish.error.generic' })}
                </Typography>
            )}
        </ActionDialogShell>
    );
}
